/**
 * @fileoverview Own bounded browser-failure projection and best-effort diagnostic delivery. `BrowserErrorBoundary.js` and HTTP transport supply observations; this module selects low-content fields and suppresses repeated reports. Diagnostic events remain distinct from application audit evidence and must not copy request bodies, private URLs, or arbitrary error text.
 */
const PAGES = new Set(['documents', 'document_chat', 'case_details', 'matters', 'calendar', 'intake',
  'admin', 'automations', 'logs', 'settings', 'inbox', 'utilities', 'login', 'onboarding']);
const ERROR_TYPES = new Set(['Error', 'TypeError', 'ReferenceError', 'SyntaxError', 'RangeError', 'URIError', 'EvalError', 'AxiosError']);
const ASSET = /(?:^|\/)((?:main|runtime-main|[0-9]+)\.[a-f0-9]+(?:\.chunk)?\.js|bundle\.js)(?::(\d+):(\d+))?/;
const recent = new Map();
let reportTimes = [];



export function browserErrorPayload(kind, error, details = {}) {
  const page = window.location.pathname.split('/')[1];
  const location = String(details.filename || error?.stack || '').match(ASSET);
  return {
    kind,
    page: PAGES.has(page) ? page : 'other',
    error_type: ERROR_TYPES.has(error?.name) ? error.name : 'Error',
    build: process.env.NODE_ENV === 'development' ? 'development' : (process.env.REACT_APP_BUILD_SHA || 'unknown'),
    correlation_id: details.correlation_id,
    api_route: details.api_route,
    status_code: details.status_code,
    asset: location?.[1],
    line: Number(details.line || location?.[2]) || undefined,
    column: Number(details.column || location?.[3]) || undefined,
  };
}



export function reportBrowserError(kind, error, details = {}) {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function' || error?.name === 'AbortError') return;
  const payload = browserErrorPayload(kind, error, details);
  const fingerprint = JSON.stringify(payload);
  const now = Date.now();
  reportTimes = reportTimes.filter(time => now - time < 60000);
  for (const [key, time] of recent) if (now - time >= 60000) recent.delete(key);
  if (reportTimes.length >= 5 || recent.has(fingerprint)) return;
  recent.set(fingerprint, now);
  reportTimes.push(now);
  const base = process.env.NODE_ENV === 'development' ? 'http://localhost:5100/api'
    : (process.env.REACT_APP_API_BASE_URL || '/api');
  try {
    Promise.resolve(window.fetch(`${base.replace(/\/$/, '')}/client-errors`, {
      method: 'POST', credentials: 'include', keepalive: true,
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    })).catch(() => {});
  } catch (_) {
    // Diagnostics must not turn a failed request into a second application failure.
  }
}



export function installBrowserErrorReporting() {
  if (window.__KC_ERROR_REPORTING_INSTALLED__) return () => {};
  window.__KC_ERROR_REPORTING_INSTALLED__ = true;
   
  const onError = event => reportBrowserError('runtime', event.error, {
    filename: event.filename, line: event.lineno, column: event.colno,
  });
   
  const onRejection = event => reportBrowserError('unhandled_rejection', event.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    delete window.__KC_ERROR_REPORTING_INSTALLED__;
  };
}
